import { useFetch } from "#app/composables/fetch";
import { defineStore } from "pinia";
import { formatText } from "~/helpers/text";
import type {
  AboutMe,
  Book,
  Course,
  DatabaseEducation,
  DatabaseSkill,
  Language,
  PersonalInformation,
  ProfessionalInterest,
} from "~/typings/database.d";
import type { Portfolio } from "~/typings/portfolio";
import type { AsyncDataRequestStatus } from "#app";

type State = {
  personalInformation: PersonalInformation | null;
  aboutMe: AboutMe | null;
  education: DatabaseEducation[];
  experiences: Portfolio.Experience[];
  languages: Language[];
  skills: DatabaseSkill[];
  status: AsyncDataRequestStatus;
  error: string | null;
  courses: Course[];
  books: Book[];
  professionalInterests: ProfessionalInterest[];
  backgroundStatus: AsyncDataRequestStatus;
  backgroundError: string | null;
};

type FetchResult<T> = {
  data: { value: T | null };
  error: { value: { message?: string } | null };
};

export type ResumeDataFetcher = <T>(
  url: string,
  options: { key: string }
) => Promise<FetchResult<T>>;

export async function fetchResumeResources(fetcher: ResumeDataFetcher) {
  const [
    personalInfoRes,
    aboutMeRes,
    experiencesRes,
    educationRes,
    languagesRes,
    skillsRes,
  ] = await Promise.all([
    fetcher<PersonalInformation>("/api/resume/personal-information", {
      key: "resume-personal-info",
    }),
    fetcher<AboutMe>("/api/resume/about-me", {
      key: "resume-about-me",
    }),
    fetcher<Portfolio.Experience[]>("/api/resume/experience", {
      key: "resume-experiences",
    }),
    fetcher<DatabaseEducation[]>("/api/resume/education", {
      key: "resume-education",
    }),
    fetcher<Language[]>("/api/resume/languages", {
      key: "resume-languages",
    }),
    fetcher<DatabaseSkill[]>("/api/resume/skills", {
      key: "resume-skills",
    }),
  ]);

  if (
    personalInfoRes.error.value ||
    aboutMeRes.error.value ||
    experiencesRes.error.value ||
    educationRes.error.value ||
    languagesRes.error.value ||
    skillsRes.error.value
  ) {
    return {
      ok: false as const,
      error:
        personalInfoRes.error.value?.message ||
        aboutMeRes.error.value?.message ||
        experiencesRes.error.value?.message ||
        educationRes.error.value?.message ||
        languagesRes.error.value?.message ||
        skillsRes.error.value?.message ||
        "Failed to load resume data",
    };
  }

  if (
    !personalInfoRes.data.value ||
    !aboutMeRes.data.value ||
    !experiencesRes.data.value ||
    !educationRes.data.value ||
    !languagesRes.data.value ||
    !skillsRes.data.value
  ) {
    return {
      ok: false as const,
      error: "No data received from API",
    };
  }

  return {
    ok: true as const,
    data: {
      personalInformation: personalInfoRes.data.value,
      aboutMe: aboutMeRes.data.value,
      experiences: experiencesRes.data.value,
      education: educationRes.data.value,
      languages: languagesRes.data.value,
      skills: skillsRes.data.value,
    },
  };
}

export const useResumeStore = defineStore("resume", {
  state: (): State => ({
    education: [],
    experiences: [],
    languages: [],
    skills: [],
    personalInformation: null,
    aboutMe: null,
    error: null,
    status: "idle",
    courses: [],
    books: [],
    professionalInterests: [],
    backgroundStatus: "idle",
    backgroundError: null,
  }),

  getters: {
    isLoading: (state): boolean => state.status === "pending",
    hasError: (state): boolean => state.status === "error",
    isReady: (state): boolean => state.status === "success",

    socialLinks: (state) => {
      if (!state.personalInformation) {
        return [];
      }

      const socialLinks = [
        ...(state.personalInformation.socialLinks ?? []),
        {
          name: state.personalInformation.email,
          displayName: "Mail me",
          url: `mailto:${state.personalInformation.email}?subject=Hola Diego, te contacto desde diegomarmol.com`,
          iconClass: "i-custom-email-outline",
          visible: true,
        },
      ];

      return socialLinks;
    },

    presentation: (state): string => {
      if (!state.personalInformation?.presentation) {
        return "";
      }
      return formatText(state.personalInformation.presentation);
    },
  },

  actions: {
    async loadResumeData() {
      // If already loaded, skip
      if (this.status === "success") {
        return;
      }

      // Set loading state
      this.status = "pending";

      try {
        const result = await fetchResumeResources(
          useFetch as unknown as ResumeDataFetcher
        );

        if (!result.ok) {
          this.status = "error";
          this.error = result.error;
          return;
        }

        this.personalInformation = result.data.personalInformation;
        this.aboutMe = result.data.aboutMe;
        this.experiences = result.data.experiences;
        this.education = result.data.education;
        this.languages = result.data.languages;
        this.skills = result.data.skills;
        this.status = "success";
        this.error = null;
      } catch (err) {
        this.status = "error";
        this.error =
          err instanceof Error ? err.message : "Unknown error occurred";
      }
    },

    async loadBackground() {
      if (this.backgroundStatus === "success") {
        return;
      }

      this.backgroundStatus = "pending";

      try {
        const { data, error } = await useFetch<{
          education: import("~/typings/database.d").DatabaseEducation[];
          courses: import("~/typings/database.d").Course[];
          books: import("~/typings/database.d").Book[];
          professionalInterests: import("~/typings/database.d").ProfessionalInterest[];
        }>("/api/resume/background", { key: "resume-background" });

        if (error.value) {
          this.backgroundStatus = "error";
          this.backgroundError =
            error.value.message || "Failed to load background data";
          return;
        }

        if (!data.value) {
          this.backgroundStatus = "error";
          this.backgroundError = "No background data received";
          return;
        }

        this.courses = data.value.courses;
        this.books = data.value.books;
        this.professionalInterests = data.value.professionalInterests;
        this.backgroundStatus = "success";
        this.backgroundError = null;
      } catch (err) {
        this.backgroundStatus = "error";
        this.backgroundError =
          err instanceof Error ? err.message : "Unknown error occurred";
      }
    },
  },
});
